// Copyright (c) 2026 Celadonsoft
// All rights reserved.

// Shared JSON-LD builders. Everything hangs off one stable `@id` so the pass is
// a single entity rather than a name repeated on every page.

// Must be byte-identical everywhere, so: no language, no path. The origin comes
// from window.location.origin; scripts/prerender.js rewrites it in snapshots.
export const COOLPASS_ENTITY_FRAGMENT = '#prague-coolpass';

// Language-independent key for the relationship. The value stays a short
// machine token - the human sentence belongs in the visible page text.
export const COOLPASS_BENEFIT_PROPERTY_ID = 'coolpassBenefit';

// The shop quotes the pass in euros; the CZK figure on the Pass/Card page is
// `price_czk` from the same catalogue record and is NOT discounted, which is a
// second reason offers belong on the shop page alone.
export const PASS_PRICE_CURRENCY = 'EUR';

// Always the catalogue's `price`, never `base_price`.
//
// `base_price` is the struck-through number. card-calculator.vue prints `price`
// in both branches and only adds `base_price` inside <s> when the two differ,
// so `price` is by construction the figure a visitor reads. Verified on the
// live page: every `72 EUR` / `116 EUR` on it sits inside an <s> element, with
// 64.8 / 104.4 shown as the actual price under a 10% anniversary offer.
//
// Worth knowing when this is wired up: production's current snapshot predates
// that promotion - it contains no <s>, no discounted figures and no promo
// banner - so crawlers are reading 72 EUR while visitors see 64.8 EUR. Within
// any one snapshot the markup and the visible text always agree, because both
// are rendered by the same app at capture time; the gap is between a snapshot
// and reality, and it closes only by re-running the prerender when prices move.
export const passOfferPrice = (product) => (product ? product.price : undefined);

// One Offer per VARIANT - duration x fare - never per SKU.
//
// The catalogue holds 32 products: each duration x fare exists twice, once as
// "Card" and once as "CoolPass", which are the two carriers of the same pass.
// They carry the same price, and the shop only asks which carrier you want
// after the price is chosen, so a second Offer would be a duplicate with an
// identical figure. 16 variants, of which two happen to share a price (2 Day
// Adult and 5 Day Child/Student are both 86.4) - which is why they are grouped
// by variant and not by price, or one of those two would vanish from markup
// while staying in the visible table.
//
// If the carriers ever stop sharing a price the model is wrong, and silently
// publishing one of the two would be worse than stopping, so that case throws.
const carrierPrice = (variantName, ...products) => {
  const prices = products.filter(Boolean).map(passOfferPrice);
  const distinct = [...new Set(prices)];

  if (distinct.length > 1) {
    throw new Error(
      `structured-data: carriers disagree on the price of "${variantName}" ` +
        `(${distinct.join(' vs ')}). One Offer cannot describe both - the ` +
        `variant needs splitting by carrier before this can be marked up.`
    );
  }

  return distinct[0];
};

// The catalogue names every product in English only - all 32 carry a single
// `en` entry and nothing else - while the shop itself is fully translated. So
// the name is rebuilt from the same strings the page prints, rather than read
// from the catalogue: card-calculator.vue turns "3 Day Prague Card" into
// "3 DENNI PASS" with exactly these replacements, and the fare label is the
// same translation key the row heading uses. STUDENT_CHILD holds a newline for
// its two-line layout, hence the whitespace collapse.
const collapse = (text) =>
  String(text || '')
    .replace(/\s+/g, ' ')
    .trim();

// Appends a node for the page itself when nothing else in the graph can link
// the page to the pass.
//
// `about` is defined on CreativeWork, so a node typed Place or
// TouristAttraction cannot carry it, and those pages ended up saying nothing
// about the pass at all - the benefit property states "Included" without
// naming what it is included in. Area and theme pages escape that only because
// their listing already gives them a CollectionPage node to hang `about` on.
//
// The page node declares no Product, so it does not bring back the Google
// product requirements that declaring one on every page triggered. It is
// skipped where the page describes the product itself, and where something
// already carries `about`, so no page ever ends up with two.
//
// Lives here rather than inline in the mixin because the condition reads a
// graph that is still being assembled: inline, it was correct only as long as
// nobody moved the block, and a comment does not survive that. As a function
// it is pinned by tests that assert the outcome - exactly one `about` carrier
// per page - rather than the order of the lines that produce it.
export const withPageNode = ({ graph, describesProduct, canonicalUrl, lang, productId }) => {
  if (!Array.isArray(graph)) return [];
  if (describesProduct) return graph;
  if (graph.some((node) => node && node.about)) return graph;

  const pageNode = {
    '@type': 'WebPage',
    // The page takes the fragment and the bare canonical stays with the entity
    // node: the ItemList entries on area and theme pages point at that bare
    // canonical so an attraction and its listing are one node, not two.
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    about: { '@id': canonicalUrl },
  };

  if (lang) pageNode.inLanguage = lang;
  if (productId) pageNode.mentions = { '@id': productId };

  return [...graph, pageNode];
};

// The rating the page prints, not the one behind it.
//
// The stored average is 4.4706 and the page renders `toFixed(1)`, so a visitor
// reads 4.5. Markup has to say what the page says, so the caller passes the
// value it already displayed rather than the raw average - one number, one
// rounding, one source.
//
// `reviewCount` is the full set, which is what the page counts too ("4.5 based
// on 238 user reviews"): the aggregate is computed over every card review, not
// over the handful currently paginated onto the screen, and it does not vary
// by language.
export const buildProductRating = ({ value, count }) => {
  const ratingValue = Number(value);
  const reviewCount = Number(count);

  if (!ratingValue || !reviewCount) return null;

  return {
    '@type': 'AggregateRating',
    ratingValue,
    reviewCount,
    bestRating: 5,
    worstRating: 1,
  };
};

// Individual reviews - only ever the ones actually rendered.
//
// This is why they are marked up on the reviews page alone: there the set is
// deterministic (sorted by date, one page at a time, and it follows the reader
// if they paginate). The home, Pass/Card and get-your-pass pages show
// `_.sample(cards, 15)`, a fresh random draw each render, so the snapshot's
// fifteen and the visitor's fifteen are different reviews - nothing there can
// honestly be marked up.
export const buildProductReviews = (reviews) => {
  if (!Array.isArray(reviews)) return [];

  // Only a missing rating disqualifies a review, because the stars are the one
  // thing a Review node has to state and a card without them cannot be
  // described. Everything else is optional and simply left off, so the node
  // count keeps matching the card count - the card renders whether or not the
  // text is there. No review in the database is missing any of these today.
  return reviews
    .filter((review) => review && review.rating)
    .map((review) => {
      const node = {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: Number(review.rating),
          bestRating: 5,
          worstRating: 1,
        },
      };

      if (collapse(review.text)) node.reviewBody = collapse(review.text);
      if (collapse(review.title)) node.name = collapse(review.title);
      if (review.name) node.author = { '@type': 'Person', name: collapse(review.name) };
      // Dates arrive as full ISO timestamps; schema.org wants the day.
      if (review.date) node.datePublished = String(review.date).slice(0, 10);

      return node;
    });
};

// The "What you get" list on the Pass/Card page, as product characteristics.
//
// No `propertyID` here, unlike the attraction benefit. That one has a real
// machine key behind it (the entry type), so `coolpassBenefit` means the same
// thing in all 17 languages. These eight have nothing but their position in a
// CMS array, and a positional `passBenefit3` would start lying the moment an
// editor reorders the list - an identifier that lies is worse than none, so
// they travel as plain name/value pairs.
//
// The node therefore differs per language, which is fine for a description:
// each language version is its own document, and the shared `@id` is what ties
// them to one product. That is a different case from sameAs, where a per
// language value would have claimed different identities. Note this is forced,
// not preferred: `additionalProperty` is defined on Place, Product and the
// value types only, so it cannot be moved onto the page node instead.
//
// Entries with no title are skipped: ar, he and jp carry eight blank ones each
// (none of which the site serves - Japanese is `ja` and is filled in).
export const buildPassBenefits = (benefits) => {
  if (!Array.isArray(benefits)) return [];

  return benefits
    .filter((benefit) => benefit && collapse(benefit.title))
    .map((benefit) => ({
      '@type': 'PropertyValue',
      name: collapse(benefit.title),
      // Falls back to the title so the node is never valueless. Dropping such
      // an entry would be worse: the template renders `title` in an <li>
      // unconditionally and only the text is behind a v-if, so a title-only
      // benefit is still a visible bullet, and skipping it would mark up seven
      // where the page shows eight. Today every entry in every served language
      // has both, and the fields are plain text - no tags, no entities,
      // checked across all 20 languages.
      value: collapse(benefit.text) || collapse(benefit.title),
    }));
};

export const buildPassOffers = ({ cards, url, labels }) => {
  if (!Array.isArray(cards) || !cards.length || !labels) return [];

  const offers = [];

  cards.forEach((card) => {
    const base = collapse(
      String(card?.name?.default || '')
        .replace('Day', labels.day)
        .replace('Prague', '')
        .replace('Card', labels.pass)
    );

    const fares = [
      { label: labels.adult, products: [card.adultCard, card.adultCardVirtual] },
      { label: labels.studentChild, products: [card.studentCard, card.studentCardVirtual] },
    ];

    fares.forEach(({ label, products }) => {
      const name = collapse(`${base} ${collapse(label)}`);
      const price = carrierPrice(name, ...products);

      if (price === undefined || price === null) return;

      const offer = {
        '@type': 'Offer',
        name,
        price,
        priceCurrency: PASS_PRICE_CURRENCY,
        availability: 'https://schema.org/InStock',
      };

      if (url) offer.url = url;

      offers.push(offer);
    });
  });

  return offers;
};

const UNIT_CODE_PERCENT = 'P1'; // UN/CEFACT Rec 20
// Attractions carry no `currency` (null on all 137 live), so this mirrors the
// card, which hardcodes the same.
const DESTINATION_CURRENCY = 'CZK';

// `additionalProperty` is defined on Place/Product, not on CreativeWork - and
// the CMS markup field may hold a page type (4 live attractions are `WebPage`).
const BENEFIT_CAPABLE_TYPES = [
  'Place',
  'TouristAttraction',
  'Museum',
  'Zoo',
  'Aquarium',
  'AmusementPark',
  'LandmarksOrHistoricalBuildings',
  'PlaceOfWorship',
  'Church',
  'Castle',
  'PerformingArtsTheater',
  'MovieTheater',
  'Park',
  'Product',
];

export const canAcceptBenefit = (type) => BENEFIT_CAPABLE_TYPES.includes(type);

// Keep the CMS type where it can hold the benefit; otherwise fall back, since
// an entry describes the attraction itself, not the page rendering it.
export const listItemType = ({ markupType, hasBenefit, fallback }) => {
  const preferred = fallback || 'TouristAttraction';

  if (!markupType) return preferred;
  if (hasBenefit && !canAcceptBenefit(markupType)) return preferred;

  return markupType;
};

// An id-based URL answers 200 with the empty SPA shell, so an ObjectId counts
// as no slug rather than as a link.
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

export const isUsableSlug = (slug) =>
  typeof slug === 'string' && slug.length > 0 && !OBJECT_ID_RE.test(slug);

// The company behind the pass. A second stable id, same idea as the product's:
// declared once, referenced by @id from everywhere else.
export const COOLPASS_ORGANIZATION_FRAGMENT = '#organization';

// Pages that represent the organisation itself. sameAs is an identity claim,
// so every entry here is a canonical, parameter-free form: the footer builds
// per-language store links through getStoreLinks(), and a /jp/ or /cz/
// storefront would assert a different profile on each language version.
//
// The Maps entry is the CID form rather than the /maps/place/... URL a browser
// produces, because that one carries session parameters (entry, g_ep) that
// change on every visit.
const COOLPASS_PROFILE_URLS = [
  'https://apps.apple.com/app/id1378275600',
  'https://play.google.com/store/apps/details?id=com.bookletia.coolpassprague',
  'https://maps.google.com/?cid=6201465390653851675',
];

// Third-party pages ABOUT the pass. These travel as `subjectOf`, not `sameAs`:
// sameAs asserts that two URLs are the same entity, which holds for a profile
// the company controls but not for an editorial page someone else publishes. A
// national tourism portal writing about the pass is a work whose subject is the
// pass - exactly what subjectOf says, and it makes no identity claim we cannot
// back. Deliberately narrow either way: a reseller's listing (GetYourGuide and
// the like) describes that reseller's offer, not this product.
const COOLPASS_PRODUCT_REFERENCE_URLS = [
  // CzechTourism's national tourism portal, dedicated page for the pass.
  'https://www.kudyznudy.cz/aktivity/prazska-turisticka-karta-prague-card',
];

// The two halves of what is sold: the physical Prague Card and the pass inside
// the app. Google asks for pictures that clearly show the product, several of
// them, and prefers a plain background - the app shot was a transparent PNG
// and is flattened onto white here. Served from public/ rather than imported
// from src/assets for the same reason the logo is: webpack content-hashes
// anything under assets, and this node has to name the same file across
// builds. Both are well past the 50,000-pixel floor (670K and 1.38M).
const COOLPASS_PRODUCT_IMAGES = ['/img/prague-card.jpg', '/img/prague-pass-app.jpg'];

export const coolpassEntityId = (origin) => `${origin}/${COOLPASS_ENTITY_FRAGMENT}`;

export const coolpassOrganizationId = (origin) => `${origin}/${COOLPASS_ORGANIZATION_FRAGMENT}`;

// `name` is the name the company is KNOWN by, not the one in the register:
// this node is what the product's `brand` points at, so a legal name here made
// the brand read as "CoolPass s.r.o.". The registered form keeps its own field.
// Taken from the terms page, which states that praguecoolpass.com is owned and
// operated by CoolPass s.r.o.
//
// The address is printed on /contact ("Office address") and matches the
// company's Google Business Profile - the same listing sameAs points at - down
// to the street number. Google requires marked-up content to be visible on the
// page, and this is.
//
// The logo is served from public/ rather than imported from src/assets, for the
// same reason the og:image fallback is: webpack content-hashes anything under
// assets, and this node has to name the same file across builds. It is the
// brand mark the app-install banner uses, and the source the 1200x630 share
// image was made from.
export const buildCoolpassOrganization = ({ origin, homeUrl }) => {
  const organization = {
    '@type': 'Organization',
    '@id': coolpassOrganizationId(origin),
    name: 'Prague CoolPass',
    legalName: 'CoolPass s.r.o.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Na Strži 2102/61a',
      addressLocality: 'Prague 4',
      postalCode: '140 00',
      addressCountry: 'CZ',
    },
    logo: {
      '@type': 'ImageObject',
      url: `${origin}/img/logo.png`,
      width: 512,
      height: 512,
    },
    sameAs: COOLPASS_PROFILE_URLS,
  };

  if (homeUrl) organization.url = homeUrl;

  return organization;
};

// Lean on purpose: price, offers and ratings belong to the Pass/Card and
// Reviews scope, and will attach to this same `@id`.
// What a page may add to the product node, and the rule for adding it.
//
// The node is emitted on all 3429 pages, so anything put here unconditionally
// appears on pages that do not show it - offers on attraction pages, a rating
// on the FAQ. That is the markup/content divergence Google penalises across a
// whole domain. So the node stays lean by default and a page contributes only
// what it actually renders, through the `seoProductFacts` hook in the seo mixin
// (same contract as `seoListAttractions`).
//
// Facts are merged as given. Callers pass whole schema.org properties -
// `offers`, `additionalProperty`, `aggregateRating` - already built.
export const buildCoolpassEntity = ({ origin, homeUrl, facts }) => {
  const entity = {
    '@type': 'Product',
    '@id': coolpassEntityId(origin),
    name: 'Prague CoolPass',
    // The physical pass is sold as "Prague Card" - same product, different
    // delivery format - so both names resolve to this one node.
    alternateName: 'Prague Card',
    // Points at the Organization node rather than an inline Brand, so product,
    // company and its profiles form one connected graph.
    brand: {
      '@id': coolpassOrganizationId(origin),
    },
    image: COOLPASS_PRODUCT_IMAGES.map((path) => `${origin}${path}`),
    subjectOf: COOLPASS_PRODUCT_REFERENCE_URLS.map((url) => ({
      '@type': 'WebPage',
      url,
    })),
  };

  if (homeUrl) entity.url = homeUrl;

  return facts ? { ...entity, ...facts } : entity;
};

// Four entry types exist, not two (live counts: FreeWithCoolpass 86,
// Discounted 44, FreeForPublic 7, TwoForOne 0). The API serialises the enum by
// name. FreeForPublic gets nothing - those are free for everyone, so there is
// no CoolPass benefit to claim. TwoForOne is matched explicitly so the first
// one created is not silently reported as "Included".
export const buildCoolpassBenefit = (attraction) => {
  if (!attraction) return null;

  switch (attraction.entryType) {
    case 'FreeForPublic':
      return null;

    case 'FreeWithCoolpass':
      return benefitProperty('Included');

    case 'TwoForOne':
      return benefitProperty('TwoForOne');

    case 'Discounted':
      return discountBenefit(attraction);

    default:
      return null;
  }
};

const benefitProperty = (value, valueReference) => {
  const property = {
    '@type': 'PropertyValue',
    propertyID: COOLPASS_BENEFIT_PROPERTY_ID,
    value,
  };

  if (valueReference) property.valueReference = valueReference;

  return property;
};

// Both discount fields are stored negative - the CMS models them as a price
// adjustment. The magnitude is what a reader means by "30% off".
const discountBenefit = (attraction) => {
  const percentage = Math.abs(Number(attraction.discountPercentage) || 0);

  if (percentage > 0) {
    return benefitProperty('Discount', {
      '@type': 'QuantitativeValue',
      value: percentage,
      unitCode: UNIT_CODE_PERCENT,
    });
  }

  // Three live attractions have a fixed amount and no percentage. Different
  // shape on purpose, so 400 CZK off is not readable as 400% off.
  const fixedAmount = Math.abs(Number(attraction.discountFixedAmount) || 0);

  if (fixedAmount > 0) {
    return benefitProperty('Discount', {
      '@type': 'MonetaryAmount',
      value: fixedAmount,
      currency: DESTINATION_CURRENCY,
    });
  }

  return benefitProperty('Discount');
};

export const buildAttractionListItem = ({ position, name, url, type, benefit, fallbackType }) => {
  const item = {
    '@type': listItemType({ markupType: type, hasBenefit: !!benefit, fallback: fallbackType }),
    name,
  };

  // Same string the attraction's own page uses as its node id, so the two
  // resolve to one node. Omitted entirely when no usable slug exists.
  if (url) {
    item['@id'] = url;
    item.url = url;
  }

  if (benefit) item.additionalProperty = [benefit];

  return {
    '@type': 'ListItem',
    position,
    item,
  };
};
